import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tree, Button, Spin, Empty } from 'antd';
import Tooltip from '../common/Tooltip';
import { EditOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useStore } from '../../store/store';
import { useDocumentKindLabel } from '../../hooks';
import RecursiveModelDot from '../common/RecursiveModelDot';
import { ItemTypes, getPrimaryDark } from '../../constants/constants';
import { scrollToItem } from '../Middle/InfinitePage';
import {
  buildProposalSectionTree,
  buildTemplateSectionTree,
} from '../../utils/sectionTree';
import { sectionEditorUrl } from '../../utils/browser';

import './LeftSide.less';

const selector = (state) => ({
  currentDocument: state.currentDocument,
  boxes: state.boxes,
  containers: state.containers,
  sections: state.sections,
  recursiveModelLabels: state.recursiveModelLabels,
  recursiveSectionRelationships: state.recursiveSectionRelationships,
  currentTemplate: state.currentTemplate,
  getTemplate: state.getTemplate,
  selectedBoxId: state.selectedBoxId,
  selectedContainer: state.selectedContainer,
  activeContainerId: state.activeContainerId,
});

// Icône par nature de nœud. Single-tone 'light' partout : le duotone n'est pas
// dans les packages npm FA Pro installés (cf. DuotoneLightIcon), le rendre via
// le macro donnait une icône floue pour les sections liées.
const kindIcon = (kind, color) => {
  if (kind === 'recursive') {
    return <FontAwesomeIcon icon={icon({ name: 'layer-group', style: 'light' })} style={{ color, fontSize: 14 }} />;
  }
  if (kind === 'possible') {
    return <FontAwesomeIcon icon={icon({ name: 'arrow-turn-down-right', style: 'light' })} style={{ color, fontSize: 12, opacity: 0.6 }} />;
  }
  return <FontAwesomeIcon icon={icon({ name: 'link', style: 'light' })} style={{ color, fontSize: 14 }} />;
};

// Libellé du tooltip par nature de nœud (préciser lié vs récursif).
const kindTooltip = (kind) => {
  if (kind === 'recursive') return 'Section récursive';
  if (kind === 'possible') return 'Section récursive possible';
  return 'Section liée';
};

// Ouvre une section dans un nouvel onglet (?tid=), comme l'ancienne liste plate.
const openSectionInTab = (e, sectionId) => {
  e.stopPropagation();
  window.open(sectionEditorUrl(sectionId), '_blank', 'noopener,noreferrer');
};

const SectionTree = () => {
  const { t } = useTranslation();
  const documentKindLabel = useDocumentKindLabel();
  const {
    currentDocument,
    boxes,
    containers,
    sections,
    recursiveModelLabels,
    recursiveSectionRelationships,
    currentTemplate,
    getTemplate,
    selectedBoxId,
    selectedContainer,
    activeContainerId,
  } = useStore(selector);
  const isTemplate = currentDocument?.type === 'template';
  const color = getPrimaryDark(isTemplate);

  // Mode proposition : dérivation synchrone depuis le contenu baké.
  const proposalRoots = useMemo(() => {
    if (isTemplate) return [];
    try {
      return buildProposalSectionTree({ boxes, containers, sections, recursiveModelLabels });
    } catch (e) {
      console.error('[SectionTree] build proposal tree failed', e);
      return [];
    }
  }, [isTemplate, boxes, containers, sections, recursiveModelLabels]);

  // Mode template : descente récursive asynchrone via getTemplate.
  const [templateRoots, setTemplateRoots] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!isTemplate) return;
    let cancelled = false;
    setLoading(true);
    buildTemplateSectionTree({
      boxes,
      sections,
      currentTemplate,
      recursiveSectionRelationships,
      recursiveModelLabels,
      getTemplate,
    })
      .then((roots) => {
        if (!cancelled) setTemplateRoots(roots);
      })
      .catch((e) => {
        console.error('[SectionTree] build template tree failed', e);
        if (!cancelled) setTemplateRoots([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTemplate, boxes, sections, currentTemplate, recursiveSectionRelationships, recursiveModelLabels]);

  const roots = isTemplate ? templateRoots : proposalRoots;

  // Map key -> nœud (pour retrouver l'ancre au clic) + treeData antd + toutes les clés.
  const { treeData, nodeByKey, allKeys } = useMemo(() => {
    const map = new Map();
    const keys = [];
    const toAntd = (node) => {
      map.set(node.key, node);
      keys.push(node.key);
      return {
        key: node.key,
        kind: node.kind,
        anchorBoxId: node.anchorBoxId,
        sectionId: node.sectionId,
        // Reporté sur le nœud antd : titleRender ne reçoit que ces champs, et
        // la puce de couleur du modèle en dépend.
        modelName: node.modelName,
        label: node.label,
        count: node.count,
        children: (node.children || []).map(toAntd),
      };
    };
    const data = (roots || []).map(toAntd);
    return { treeData: data, nodeByKey: map, allKeys: keys };
  }, [roots]);

  // Tout déplié par défaut ; l'utilisateur peut replier (état contrôlé pour que
  // le rechargement async en mode template ré-étende le nouvel arbre).
  const [expandedKeys, setExpandedKeys] = useState([]);
  useEffect(() => {
    setExpandedKeys(allKeys);
  }, [allKeys]);

  const firstBoxOfContainer = (cont) => {
    if (!cont || !Array.isArray(cont.columnsIds)) return null;
    return boxes.find((b) => cont.columnsIds.includes(b.columnId)) || null;
  };
  const pathTip = (arr) =>
    Array.isArray(arr) && arr.length ? String(arr[arr.length - 1]) : null;

  // Résout la box vers laquelle scroller. L'enfant d'une section a
  // container.sectionId = id du PARENT (héritage de fromSectionId), donc on
  // matche d'abord la POINTE de sectionPath (= id de section réel, distingue
  // parent et enfant), puis container.sectionId (top-level), puis l'ancre.
  const resolveSectionBox = (node) => {
    const sid = node.sectionId != null ? String(node.sectionId) : null;
    if (sid) {
      const byPath = containers.find((c) => pathTip(c?.sectionPath) === sid);
      if (byPath) {
        const box = firstBoxOfContainer(byPath);
        if (box) return box;
      }
      const bySection = containers.find((c) => String(c?.sectionId) === sid);
      if (bySection) {
        const box = firstBoxOfContainer(bySection);
        if (box) return box;
      }
    }
    if (node.anchorBoxId) {
      const box = boxes.find((b) => b.id === node.anchorBoxId);
      if (box) return box;
    }
    if (sid) {
      return (
        boxes.find((b) => pathTip(b?.sectionPath) === sid) ||
        boxes.find(
          (b) =>
            String(b.sectionId) === sid || String(b.fromSectionId) === sid
        ) ||
        null
      );
    }
    return null;
  };

  // Contrôlé : la sélection ne vient pas que du clic ici (cf. l'effet plus
  // bas, qui la fait suivre le clic dans le document, comme le Sommaire).
  const [highlightedKeys, setHighlightedKeys] = useState([]);

  const onSelect = (keys) => {
    setHighlightedKeys(keys);
    const node = keys.length ? nodeByKey.get(keys[0]) : null;
    if (!node) return;
    const box = resolveSectionBox(node);
    if (box) scrollToItem(box);
  };

  // Surbrillance miroir : cliquer une section dans le document sélectionne le
  // nœud correspondant ici, même repère que celui déjà en place dans le
  // Sommaire (SummarySide). Identité de la section cliquée, dans l'ordre de
  // précision décroissante — même logique que resolveSectionBox, en sens
  // inverse (nœud -> box) : ici on part de la box/container sélectionné pour
  // retrouver le nœud.
  useEffect(() => {
    const selectedBox = selectedBoxId ? boxes.find((b) => b.id === selectedBoxId) : null;
    // activeContainerId : ce que suit le panel droit (SectionContextPanel) —
    // un clic dans un titre/texte d'une section bakée ne pose pas toujours
    // selectedBoxId ni selectedContainer.
    const containerId = selectedContainer?.id ?? activeContainerId;
    const activeContainer = selectedBox
      ? containers.find((c) => c.columnsIds.includes(selectedBox.columnId))
      : containerId
        ? containers.find((c) => c.id === containerId)
        : null;

    // Proposition : les nœuds sont keyés par le chemin complet (sectionPath),
    // plus précis que l'id seul (une même section peut vivre sous deux parents).
    const path = Array.isArray(selectedBox?.sectionPath) && selectedBox.sectionPath.length
      ? selectedBox.sectionPath
      : activeContainer?.sectionPath;
    const pathKey = Array.isArray(path) && path.length ? path.map(String).join('>') : null;
    if (pathKey && nodeByKey.has(pathKey)) {
      setHighlightedKeys([pathKey]);
      return;
    }

    // Ancre récursive imbriquée (RecursiveModelConfigModal) : pas de
    // container injecté, la cible est portée par la box elle-même.
    const anchorIdentity =
      selectedBox?.type === ItemTypes.RECURSIVE_SECTION && selectedBox.content?.childTemplateId != null
        ? String(selectedBox.content.childTemplateId)
        : null;
    const identity =
      anchorIdentity ??
      pathTip(selectedBox?.sectionPath) ??
      pathTip(activeContainer?.sectionPath) ??
      (activeContainer?.sectionId != null ? String(activeContainer.sectionId) : null) ??
      (selectedBox?.fromSectionId != null ? String(selectedBox.fromSectionId) : null);

    if (identity == null) {
      setHighlightedKeys([]);
      return;
    }
    let matchedKey = null;
    for (const [key, node] of nodeByKey) {
      if (node.sectionId != null && String(node.sectionId) === identity) {
        matchedKey = key;
        break;
      }
    }
    setHighlightedKeys(matchedKey ? [matchedKey] : []);
  }, [selectedBoxId, selectedContainer, activeContainerId, boxes, containers, nodeByKey]);

  const titleRender = (node) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>
      {/* Puce du modèle récursif, identique à celle du bandeau de contexte du
          panel droit : c'est ce repère commun qui relie les deux vues. */}
      <RecursiveModelDot
        modelName={node.modelName}
        label={recursiveModelLabels?.[node.modelName] ?? node.modelName}
      />
      <Tooltip title={kindTooltip(node.kind)} placement="top" mouseEnterDelay={0.3}>
        <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>
          {kindIcon(node.kind, color)}
        </span>
      </Tooltip>
      <Tooltip title={node.label} placement="right" mouseEnterDelay={0.3}>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: node.kind === 'possible' ? 'rgba(11,34,57,0.55)' : color,
            fontStyle: node.kind === 'possible' ? 'italic' : 'normal',
            // Sélection portée en inline, comme le Sommaire : .custom-tree
            // (LeftSide.less) rend transparent le fond du nœud sélectionné.
            ...(highlightedKeys.includes(node.key) ? { fontWeight: 'bold' } : {}),
          }}
        >
          {node.label}
        </span>
      </Tooltip>
      {node.count > 1 && (
        <span
          style={{
            flexShrink: 0,
            fontSize: 10,
            fontWeight: 700,
            color: '#FFFFFF',
            background: color,
            borderRadius: 8,
            padding: '0 6px',
            lineHeight: '15px',
          }}
        >
          ×{node.count}
        </span>
      )}
      {node.sectionId && (
        <Tooltip title={t('edit-section')} placement="top" mouseEnterDelay={0.3}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => openSectionInTab(e, node.sectionId)}
            style={{ color, padding: '0 4px', minWidth: 'auto', flexShrink: 0 }}
            aria-label={t('edit-section')}
          />
        </Tooltip>
      )}
    </div>
  );

  return (
    <div style={{ padding: '4px 0' }}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin size="small" />
        </div>
      ) : treeData.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={`Aucune section : ${documentKindLabel} ne comporte aucune section.`}
        />
      ) : (
        <Tree
          className="custom-tree section-tree"
          blockNode
          treeData={treeData}
          titleRender={titleRender}
          onSelect={onSelect}
          selectable
          selectedKeys={highlightedKeys}
          showLine={{ showLeafIcon: false }}
          expandedKeys={expandedKeys}
          onExpand={setExpandedKeys}
          switcherIcon={
            <FontAwesomeIcon icon={icon({ name: 'chevron-down', style: 'solid' })} style={{ fontSize: 10 }} />
          }
          style={{ background: 'transparent' }}
        />
      )}
    </div>
  );
};

export default React.memo(SectionTree);
